import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import type { AudioRecorderStatus } from '@backrs_ui/common-ui/src/hooks/useAudioRecorder';

import { CircularProgress, IconButton, SxProps, Theme } from '@mui/material';
import { keyframes } from '@mui/system';

export type RecordButtonProps = Readonly<{
  id: string;
  status: AudioRecorderStatus;
  /** Resolved for the current `status`, so each surface keeps its own copy. */
  label: string;
  onStart: () => void;
  onStop: () => void;
  /** Applied on top of idle styling; recording and transcribing styling still wins. */
  sx?: SxProps<Theme>;
  disabled?: boolean;
  onMouseDown?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

/**
 * Mic that toggles a recording and reports the recorder's three states. Icon and
 * spinner inherit `currentColor`, so callers set idle colour through `sx`.
 */
export const RecordButton = ({
  id,
  status,
  label,
  onStart,
  onStop,
  sx,
  disabled,
  onMouseDown,
}: RecordButtonProps) => {
  const isRecording = status === 'recording';
  const isTranscribing = status === 'transcribing';

  return (
    <IconButton
      id={id}
      size="small"
      aria-label={label}
      aria-pressed={isRecording}
      // Stop has to stay reachable even when the surrounding surface disables input.
      disabled={(disabled && !isRecording) || isTranscribing}
      onMouseDown={onMouseDown}
      onClick={isRecording ? onStop : onStart}
      sx={[...(Array.isArray(sx) ? sx : [sx]), recordingSx(isRecording)]}
    >
      {isTranscribing ? (
        <CircularProgress size={20} sx={inheritColorSx} />
      ) : (
        <AppIcon
          iconName={isRecording ? 'Square' : 'Microphone01'}
          diameter="20px"
          color={isRecording ? 'Colors/Foreground/fg-error-primary' : 'currentColor'}
          fill={isRecording ? 'Colors/Foreground/fg-error-primary' : undefined}
        />
      )}
    </IconButton>
  );
};

const recordingPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

const recordingSx =
  (isRecording: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    ...(isRecording && {
      backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
      animation: `${recordingPulse} 1.5s ease-in-out infinite`,
      '&:hover': {
        backgroundColor: backrs.colors['Colors/Background/bg-error-primary'],
      },
    }),
  });

const inheritColorSx: SxProps<Theme> = { color: 'inherit' };
